import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scan, X, CheckCircle, Shield, Play, KeyRound } from 'lucide-react';
interface AdminAuthModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title?: string;
  description?: string;
}
type AuthMode = 'rfid' | 'pin';
export function AdminAuthModal({
  isVisible,
  onClose,
  onSuccess,
  title = 'Administrator-Berechtigung erforderlich',
  description = 'Bitte scannen Sie Ihren Admin-RFID-Chip'
}: AdminAuthModalProps) {
  const [authSuccess, setAuthSuccess] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('rfid');
  const [pin, setPin] = useState('');
  const handleDemo = () => {
    setAuthSuccess(true);
    setTimeout(() => {
      onSuccess();
      setAuthSuccess(false);
      setPin('');
    }, 2000);
  };
  const handlePinChange = (value: string) => {
    // Only allow numbers and max 8 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 8);
    setPin(numericValue);
    // Auto-submit when 8 digits are entered
    if (numericValue.length === 8) {
      // In production, verify PIN against backend
      handleDemo();
    }
  };
  const handleModeSwitch = () => {
    setAuthMode(authMode === 'rfid' ? 'pin' : 'rfid');
    setPin('');
  };
  return <AnimatePresence data-id="element-4">
      {isVisible && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose} data-id="element-5" />
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-id="element-6">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} className="w-full max-w-md" data-id="element-7">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-8">
                {!authSuccess ? <>
                    <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6" data-id="element-9">
                      <div className="flex items-center justify-between" data-id="element-10">
                        <div className="flex items-center gap-3" data-id="element-11">
                          <Shield className="w-6 h-6 text-white" strokeWidth={2} data-id="element-12" />
                          <h3 className="text-xl font-semibold text-white" data-id="element-13">
                            Admin-Zugriff
                          </h3>
                        </div>
                        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors" data-id="element-14">
                          <X className="w-5 h-5 text-white" strokeWidth={2} data-id="element-15" />
                        </button>
                      </div>
                    </div>

                    <div className="p-8 space-y-6" data-id="element-16">
                      <div className="text-center space-y-3" data-id="element-17">
                        <h4 className="text-lg font-medium text-gray-900" data-id="element-18">
                          {title}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-19">
                          {authMode === 'rfid' ? description : 'Bitte geben Sie Ihre 8-stellige Admin-PIN ein'}
                        </p>
                      </div>

                      {authMode === 'rfid' ? <div className="flex justify-center py-6" data-id="element-20">
                          <div className="relative" data-id="element-21">
                            <div className="absolute inset-0 bg-purple-50 rounded-3xl blur-2xl opacity-40" data-id="element-22" />
                            <div className="relative bg-gradient-to-br from-gray-50 to-purple-50/30 rounded-2xl p-8 border border-gray-200/50 shadow-lg" data-id="element-23">
                              <Scan className="w-24 h-24 text-purple-600" strokeWidth={1.5} data-id="element-24" />
                            </div>
                          </div>
                        </div> : <div className="space-y-4" data-id="element-25">
                          <div className="flex justify-center" data-id="element-26">
                            <div className="p-4 bg-purple-50 rounded-2xl" data-id="element-27">
                              <KeyRound className="w-16 h-16 text-purple-600" strokeWidth={1.5} data-id="element-28" />
                            </div>
                          </div>

                          <div className="space-y-2" data-id="element-29">
                            <label className="block text-sm font-medium text-gray-700 text-center" data-id="element-30">
                              Admin-PIN
                            </label>
                            <input type="password" inputMode="numeric" value={pin} onChange={e => handlePinChange(e.target.value)} placeholder="••••••••" maxLength={8} className="w-full px-4 py-3 text-center text-2xl tracking-widest border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" autoFocus data-id="element-31" />
                            <p className="text-xs text-center text-gray-500" data-id="element-32">
                              {pin.length} / 8 Ziffern
                            </p>
                          </div>
                        </div>}

                      <div className="pt-4 border-t border-gray-200" data-id="element-33">
                        <button onClick={handleModeSwitch} className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" data-id="element-34">
                          {authMode === 'rfid' ? <>
                              <KeyRound className="w-4 h-4" strokeWidth={2} data-id="element-35" />
                              Stattdessen PIN eingeben
                            </> : <>
                              <Scan className="w-4 h-4" strokeWidth={2} data-id="element-36" />
                              Stattdessen RFID scannen
                            </>}
                        </button>
                      </div>

                      <p className="text-xs text-center text-gray-500" data-id="element-37">
                        {authMode === 'rfid' ? 'Warten auf Admin-RFID-Chip...' : 'PIN wird automatisch geprüft'}
                      </p>
                    </div>
                  </> : <>
                    <div className="bg-gradient-to-r from-green-600 to-green-700 p-6" data-id="element-38">
                      <h3 className="text-xl font-semibold text-white text-center" data-id="element-39">
                        Zugriff gewährt
                      </h3>
                    </div>

                    <div className="p-8 space-y-6" data-id="element-40">
                      <div className="text-center space-y-4" data-id="element-41">
                        <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 200
                  }} data-id="element-42">
                          <CheckCircle className="w-24 h-24 text-green-600 mx-auto" strokeWidth={1.5} data-id="element-43" />
                        </motion.div>
                        <div className="space-y-2" data-id="element-44">
                          <h4 className="text-2xl font-semibold text-gray-900" data-id="element-45">
                            Max Mustermann
                          </h4>
                          <div className="flex items-center justify-center gap-2" data-id="element-46">
                            <Shield className="w-4 h-4 text-purple-600" strokeWidth={2} data-id="element-47" />
                            <span className="text-sm font-medium text-purple-600" data-id="element-48">
                              Administrator
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </motion.div>
          </div>

          {/* Demo Button - only show for RFID mode */}
          {!authSuccess && authMode === 'rfid' && <button onClick={handleDemo} className="fixed bottom-6 right-6 z-[100] flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-purple-600 text-gray-700 hover:text-white rounded-full text-sm font-medium transition-colors shadow-sm border border-gray-200 hover:border-purple-600" aria-label="Admin Demo" data-id="element-49">
              <Play className="w-4 h-4" strokeWidth={2} data-id="element-50" />
              Demo
            </button>}
        </>}
    </AnimatePresence>;
}